import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PRICING, BRAND } from "@/lib/constants";
import { Check } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function Pricing() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-[#F9FAFB] py-16 md:py-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-[#1A1A1A] text-center tracking-tight mb-12 md:mb-16">
          {PRICING.heading}
        </h2>

        {/* Pricing card */}
        <div
          ref={ref}
          data-testid="pricing-card"
          className={`bg-white border-2 border-[#20611B] rounded-2xl p-8 md:p-10 shadow-lg relative overflow-hidden transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Decorative top bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#20611B]" />

          <div className="text-center">
            <Badge
              data-testid="pricing-badge"
              className="bg-[#D4A84B] text-white border-none text-xs font-semibold uppercase tracking-wider px-4 py-1 mb-4"
            >
              {PRICING.badge}
            </Badge>

            <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-2">
              {PRICING.planName}
            </h3>

            <div className="mb-2">
              <span
                data-testid="pricing-price"
                className="font-heading text-5xl md:text-6xl font-bold text-[#D4A84B]"
              >
                {PRICING.price}
              </span>
              <span className="text-[#6B7280] text-lg ml-1">
                {PRICING.priceSuffix}
              </span>
            </div>

            <p className="text-sm text-[#6B7280] mb-8">{PRICING.trial}</p>
          </div>

          {/* Feature checklist */}
          <div className="space-y-3 mb-8">
            {PRICING.features.map((feature, i) => (
              <div
                key={feature}
                data-testid={`pricing-feature-${i}`}
                className="flex items-center gap-3"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D8FFB8] shrink-0">
                  <Check className="h-3 w-3 text-[#20611B]" />
                </span>
                <span className="text-sm text-[#1A1A1A]">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center space-y-3">
            <Link to={BRAND.signupPath}>
              {/* Stripe checkout will connect here */}
              <Button
                data-testid="pricing-cta"
                className="w-full bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide"
              >
                {PRICING.cta}
              </Button>
            </Link>
            <p className="text-sm text-[#6B7280]">{PRICING.ctaSubtext}</p>
          </div>
        </div>

        {/* Setup callout */}
        <div
          data-testid="setup-callout"
          className="mt-8 bg-white border border-gray-200 rounded-xl p-6 text-center"
        >
          <p className="text-sm text-[#6B7280] leading-relaxed max-w-lg mx-auto">
            {PRICING.setupCallout}
          </p>
        </div>
      </div>
    </section>
  );
}
